import { useQuery as useTanstackQuery } from '@tanstack/react-query';
import { sortBy } from 'lodash';
import mixpanel from 'mixpanel-browser';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from 'urql';

import { useIsRevelioAdmin } from '@revelio/auth';
import { DataBuilderTrackingEvents } from '@revelio/core';

import { useKibanaLogger } from '../../../../shared/hooks/kibana-logger/kibana-logger';
import { AllClientsQuery } from '../../../adminRewrite/userOperations';
import { fetchDataBuilderResults } from '../../api/data-builder-api';
import { useBuildDataBuilderPayload } from '../../api/useBuildDataBuilderPayload';
import { useDataBuilderQuery } from '../../api/useDataBuilderQuery';
import {
  type CompanySelectionGateStatus,
  selectCompanyMapperGateStatus,
  selectDeliverableClient,
  selectDeliverableName,
  useDataBuilderActions,
  useDataBuilderSelector,
  useDataBuilderStore,
} from '../../data-builder.store';
import { buildDataBuilderSubmitProps } from '../../data-builder-analytics';
import { submitDeliverableWithLogging } from './submit-deliverable';
import { useNavigateToDelivery } from './useNavigateToDelivery';

export type VerifyStatus =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'success' }
  | { status: 'error'; error: unknown };

export type ConfirmationFormValues = {
  client: string;
  name: string;
};

export type ClientOption = { value: string; label: string };

type UseVerifySubmitResult = {
  control: ReturnType<typeof useForm<ConfirmationFormValues>>['control'];
  register: ReturnType<typeof useForm<ConfirmationFormValues>>['register'];
  errors: ReturnType<
    typeof useForm<ConfirmationFormValues>
  >['formState']['errors'];
  clientOptions: ClientOption[];
  isRevelioAdmin: boolean;
  verifyStatus: VerifyStatus;
  handleConfirmationSubmit: () => void;
  submitButtonText: string;
  errorMessage: string;
  canSubmit: boolean;
  companySelectionGateStatus: CompanySelectionGateStatus;
};

export const useVerifySubmit = (): UseVerifySubmitResult => {
  const { isRevelioAdmin } = useIsRevelioAdmin();
  const delayedNavigateToDelivery = useNavigateToDelivery();
  const { data } = useDataBuilderQuery();
  const { data: rawSchema } = useTanstackQuery({
    queryKey: ['data-builder'],
    queryFn: fetchDataBuilderResults,
  });
  const { kibanaLogger } = useKibanaLogger();

  const [{ data: clientsData }] = useQuery({
    query: AllClientsQuery,
    pause: !isRevelioAdmin,
  });

  const storedName = useDataBuilderSelector(selectDeliverableName);
  const storedClient = useDataBuilderSelector(selectDeliverableClient);
  const companySelectionGateStatus = useDataBuilderSelector(
    selectCompanyMapperGateStatus
  );
  const { setDeliverableName, setDeliverableClient } = useDataBuilderActions();

  const clientOptions: ClientOption[] = sortBy(clientsData?.clients, [
    'client_name',
  ])
    .map((client) => {
      const clientName = client?.client_name;
      return {
        label: typeof clientName === 'string' ? clientName : '',
        value: typeof clientName === 'string' ? clientName : '',
      };
    })
    .filter((option) => option.value !== '');

  const {
    control,
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<ConfirmationFormValues>({
    defaultValues: {
      client: storedClient,
      name: storedName,
    },
  });

  const watchedName = watch('name');
  const watchedClient = watch('client');

  useEffect(() => {
    setDeliverableName(watchedName?.trim() ?? '');
  }, [watchedName, setDeliverableName]);

  useEffect(() => {
    setDeliverableClient(watchedClient ?? '');
  }, [watchedClient, setDeliverableClient]);

  const payloadClient = isRevelioAdmin ? watchedClient || null : null;
  const payloadName = watchedName?.trim() ?? '';

  const { build } = useBuildDataBuilderPayload({
    client: payloadClient,
    name: payloadName,
    datasets: data?.datasets,
  });
  const [verifyStatus, setVerifyStatus] = useState<VerifyStatus>({
    status: 'idle',
  });

  const handleSubmitDeliverable = async () => {
    if (
      verifyStatus.status === 'pending' ||
      verifyStatus.status === 'success'
    ) {
      return;
    }

    if (rawSchema === undefined) {
      return;
    }

    const payloadResult = build(rawSchema);
    if (payloadResult === null) {
      mixpanel.track(DataBuilderTrackingEvents.SUBMIT_FAILED, {
        operation: 'build_payload',
        error_type: 'BuildPayloadError',
        error_message: 'Failed to build payload',
        page: 'data builder',
        page_type: 'databuilder_flow',
      });
      setVerifyStatus({
        status: 'error',
        error: new Error('Failed to build payload'),
      });
      return;
    }

    setVerifyStatus({ status: 'pending' });
    try {
      await submitDeliverableWithLogging(
        payloadResult.payload,
        kibanaLogger,
        'DataBuilder.useVerifySubmit'
      );
      mixpanel.track(
        DataBuilderTrackingEvents.SUBMITTED,
        buildDataBuilderSubmitProps(useDataBuilderStore.getState(), {
          isRevelioAdmin,
          submitVia: 'direct',
        })
      );
      setVerifyStatus({ status: 'success' });
      await delayedNavigateToDelivery();
    } catch (error: unknown) {
      mixpanel.track(DataBuilderTrackingEvents.SUBMIT_FAILED, {
        operation: 'submit',
        error_type: error instanceof Error ? error.name : 'Unknown',
        error_message: error instanceof Error ? error.message : String(error),
        page: 'data builder',
        page_type: 'databuilder_flow',
      });
      setVerifyStatus({ status: 'error', error });
    }
  };

  const handleConfirmationSubmit = handleSubmit(() => {
    void handleSubmitDeliverable();
  });

  const submitButtonText = (() => {
    if (verifyStatus.status === 'pending') {
      return 'Submitting...';
    }
    return 'Submit';
  })();

  const errorMessage =
    verifyStatus.status === 'error' && verifyStatus.error instanceof Error
      ? verifyStatus.error.message
      : 'An error occurred.';

  return {
    control,
    register,
    errors,
    clientOptions,
    isRevelioAdmin,
    verifyStatus,
    handleConfirmationSubmit,
    submitButtonText,
    errorMessage,
    canSubmit:
      verifyStatus.status !== 'pending' &&
      verifyStatus.status !== 'success' &&
      rawSchema !== undefined &&
      companySelectionGateStatus === 'idle',
    companySelectionGateStatus,
  };
};
